import type { TargettedEvent } from '@suntex.core/animation';
import {
  Chance,
  LoadUtil,
  TestUtil,
  Timer,
  UIUtil,
} from '@suntex.core/animation';
import type { Color } from '@suntex.core/color';
import type { GameFrame } from '@suntex.modules/core';
import { ValueText, createGameFrame } from '@suntex.modules/core';

import { globalToLocalXY } from '../compat';
import { VIF_BOARD_SOUND_FILES } from '../data/vifSounds';
import type {
  VifBlockGameStage,
  VifBlocker,
  VifEndScreenSymbol,
  VifGameProgArgs,
  VifTestWindow,
  VifTestWindowCell,
  VifTileHalf,
} from '../types';

import type { BoardGeometry } from './BoardGeometry';
import {
  mouseToCell,
  pickDistinctIndices,
  shuffleAvoidingFixedPositions,
} from './boardMath';
import { cellAt } from './grid';

/**
 * Shared tile-matching board-game state machine used by both
 * `BlockBoardEngine` (4x4, no rules text) and `RulesBoardEngine` (3xN, with
 * per-game rules text), hoisting every method/field that is identical or
 * near-identical between the two, leaving genuine per-game differences as
 * protected abstract members or small hook methods that subclasses
 * implement/override.
 *
 * `TStage` is the concrete game-stage export-root type. Each of the four
 * concrete game classes (`VIFAddSub`, `VIFMulDiv`, `VIFRulesAddSub`,
 * `VIFRulesMulDiv`) extends `BlockBoardEngine`/`RulesBoardEngine` directly
 * and overrides the protected hooks declared below with its own body,
 * reading/writing engine state (`this.topEQs`, etc.) as plain `this` access.
 *
 * NOTE: this class intentionally does NOT carry `@loggedContext(...)` -
 * TypeScript's legacy class decorators can't target an abstract class's
 * constructor type (`Unable to resolve signature of class decorator` /
 * `Decorator function return type ... not assignable`). The decorator is
 * applied to each concrete game class instead. This means methods defined
 * only here won't be auto-logged by `autolog` (it only wraps the concrete
 * class's own prototype methods, not inherited ones) - an accepted
 * trade-off, not worked around.
 */
export abstract class BoardGameEngine<TStage extends VifBlockGameStage> {
  protected gameFrame!: GameFrame;
  protected gameStage!: TStage;

  protected startSeconds: number[] = [];
  protected clockSeconds = 0;
  protected readonly clockTimer = new Timer(1000, () => this.onTick());

  protected gameFlag = 0;
  protected playerId = 0;
  protected vifStage = 1;

  /**
   * The current round's equation/answer pools. Declared here (rather than
   * only on `BlockBoardEngine`) so the base's block-flavored `nextProblem`
   * and `fillTopHalf`/`fillBottomAnswer` hook defaults can read/write them.
   * `RulesBoardEngine` declares the same three fields `protected` (widened
   * from `private`, a visibility-only change) so it can keep populating them
   * itself without colliding with this declaration.
   */
  protected topEQs: string[] = [];
  protected bottomAnswers: string[] = [];
  protected tEqValues: (string | number)[] = [];

  protected tileOrigAnswers: (string | number)[] = [];
  protected dragging = false;
  protected draggingPiece: VifTileHalf | null = null;
  protected board!: VifTestWindow;
  protected mobilePiece!: VifTestWindowCell;
  // Populated with the correct grid dimensions in each subclass's
  // `nextProblem()`; this initializer only needs to be an empty array.
  protected tileLocked: number[][] = [];

  protected currRow: number | null = null;
  protected currCol: number | null = null;
  protected startRow: number | null = null;
  protected startCol: number | null = null;
  /**
   * The dragged piece's original position and initial pointer offset,
   * recorded on mouse-down. Only used by the base's `onBoardMouseDown` (the
   * block-flavored default); `RulesBoardEngine` overrides `onBoardMouseDown`
   * and never touches these.
   */
  protected startX = 0;
  protected startY = 0;
  protected startmX = 0;
  protected startmY = 0;
  protected intCorrect = 0;
  protected intLayer = 1;
  protected blocker!: VifBlocker;
  protected blockerX = 0;
  protected blockerY = 0;
  protected blockerH = 0;
  protected blockerW = 0;

  protected readonly animMs = 150;

  constructor(
    protected readonly args: VifGameProgArgs,
    private readonly jiggleRotationRange: number,
  ) {}

  /* -------------------------------------------------------------------
   * Per-game abstract members (protected)
   * ------------------------------------------------------------------- */

  /** Builds the game frame + gameStage and runs the per-game init sequence. */
  protected abstract initGame(): void;

  /** Block: game-specific color (overridden per game). Rules: a fixed subclass color. */
  protected abstract get correctColor(): Color;

  /** Number of grid rows (Block: 4, Rules: 3). */
  protected abstract get gridRows(): number;

  /** Number of grid columns (Block: 4, Rules: `intColumns`). */
  protected abstract get gridCols(): number;

  /**
   * The hit-test geometry for this game's grid, used by the base's
   * geometry-driven `getMouseCoordinates`/`onBoardMouseMove`. `RulesBoardEngine`
   * overrides both of those methods directly instead, so its `geometry` is
   * never consulted, but it still must supply one to satisfy the base type.
   */
  protected abstract get geometry(): BoardGeometry;

  /** The tile-back frame set by `resetAllTilesToFirstFrame`. */
  protected abstract tileBackFrame(): number;

  /** The `resetAllTiles(true)` activation branch (differs entirely per game). */
  protected abstract onActivateAfterReset(): void;

  /** The bottom-half tile frame set on a correct match/bonus. */
  protected abstract bottomHalfCorrectFrame(): number;

  /** Plays the "correct" feedback sound (differs in implementation between Block and Rules). */
  protected abstract playCorrectSound(): void;

  /** Reads the expected answer value at a flattened grid index. */
  protected abstract tEqValueAt(index: number): string | number;

  /** Handles what happens once all tiles for a layer are correctly matched. */
  protected abstract checkForEnd(): void;

  /** Swaps the dragged tile with the one under the pointer and checks it. */
  protected abstract tradePiece(): void;

  /* -------------------------------------------------------------------
   * Per-game hooks with a sensible shared default
   * ------------------------------------------------------------------- */

  /** Called first in `stopGame()`. Rules overrides to call `stopClockTimer()`. */
  protected onStopGameBeginHook(): void {}

  /** The second arg to `onComplete`. Rules overrides to call `computeOperation()`. */
  protected computeCompletionOperand(): number {
    return this.gameFlag;
  }

  /** Called at the end of `timeOut()`. Rules overrides to call its `timeOutReset()`. */
  protected onTimedOut(): void {
    this.resetGame();
  }

  /** Extra guard on trading a piece. Rules overrides to call its `canTradePieceHook()`. */
  protected canTradePiece(): boolean {
    return true;
  }

  /** Extra per-tile work in `resetAllTiles`'s clearing loop. Rules clears `jiggleEnabled`. */
  protected onResettingTile(_tile: VifTestWindowCell): void {}

  /** Extra bonus-sound-queue hook in `checkForBonus`. Rules overrides to call its own hook. */
  protected onBonusSoundQueue(): void {}

  /* -------------------------------------------------------------------
   * Per-game hooks for the block-flavored `startGame`/`setupGame`/
   * `nextProblem`/`onBoardMouseDown`/`runAnimation` bodies hoisted onto this
   * base class. `BlockBoardEngine` subclasses (`VIFAddSub`, `VIFMulDiv`)
   * override most of these directly with their own bodies. `RulesBoardEngine`
   * never calls these (it overrides the methods that would call them), so
   * its defaults are irrelevant to Rules behavior.
   * ------------------------------------------------------------------- */

  /** Optional extra work in `startGame`, right after `UIUtil.show(gameStage, 500)`. */
  protected onStartGameBegin(): void {}

  /** Optional extra work in `startGame`, right before `UIUtil.hide(gameStage.mPiece)`. */
  protected onBeforeHideMobilePiece(): void {}

  /** The tail of `setupGame()`, after the shared `intLayer = gameFrame.game` assignment. */
  protected onSetupGame(): void {}

  /**
   * `nextProblem`'s clock-start condition. Block: `gameFrame.round != 1`.
   * Rules (kept on `RulesBoardEngine`'s own `nextProblem` override, not via
   * this hook): `startSeconds[round - 1] != 0`.
   */
  protected shouldStartClock(): boolean {
    return this.gameFrame.round != 1;
  }

  /** The differing "which data set gets used" branch inside `nextProblem`. */
  protected populateProblem(): void {}

  /**
   * Indices already used for the current layer, to exclude from
   * `pickDistinctIndices` in `nextProblem`. Block: no exclusions.
   */
  protected chosenExclude(): ReadonlySet<number> | undefined {
    return undefined;
  }

  /** Records which indices were chosen this round, for future exclusion. Block: no-op. */
  protected recordChosen(_indices: number[]): void {}

  /** Fills a single grid cell's top-half (equation) text in `nextProblem`. */
  protected fillTopHalf(row: number, col: number, chosenIndex: number): void {
    const settingTile = cellAt(this.board, row, col);
    ValueText.displayText(settingTile.text3, this.topEQs[chosenIndex], 2);
  }

  /** Fills a single bottom-answer tile's text in `nextProblem`. */
  protected fillBottomAnswer(
    row: number,
    col: number,
    value: string | number,
  ): void {
    ValueText.displayText(cellAt(this.board, row, col).t.textb, value);
  }

  /** The tail of `nextProblem` (meter/scoreboard updates). Must set `board.enabled = true`. */
  protected finalizeNextProblem(): void {}

  /** Run once per round, the first time `nextProblem` runs after `startGame`. Block: never fires. */
  protected onNextProblemFirstTime(): void {}

  /** Extra guard on starting a drag, in `onBoardMouseDown`. Block: always allowed. */
  protected canStartDrag(): boolean {
    return true;
  }

  /** Extra work right before the mobile piece is scaled/faded in on drag start. Block: no-op. */
  protected onDragStart(): void {}

  run(): void {
    const { api, exportRoot, params } = this.args;
    this.gameFrame = createGameFrame({
      api,
      exportRoot,
      params: {
        playerId: params.playerId,
        moduleId: params.moduleId,
        moduleVariant: params.moduleVariant,
        preview: params.preview,
        soundDirectory: params.soundDirectory,
        hubUrl: params.hubUrl,
      },
    });
    this.gameStage = exportRoot.gameStage as TStage;

    this.gameFrame.init();

    this.initGame();
  }

  protected continueVIF(_value?: unknown): void {
    const { onComplete } = this.args;
    if (typeof onComplete === 'function') {
      onComplete(this.playerId, this.computeCompletionOperand(), this.vifStage);
    } else {
      TestUtil.log('videoComplete function not found on window');
    }
  }

  protected stopGame(): void {
    this.onStopGameBeginHook();

    const continueBtn = (this.gameFrame.endScreen as VifEndScreenSymbol)
      .continueBtn;
    if (this.gameFrame.preview) {
      UIUtil.hide(this.gameFrame.practiceMenu.moreBtn);
      UIUtil.hide(this.gameFrame.endScreen.moreBtn);
      UIUtil.hide(this.gameFrame.endScreen.nextBtn);
      UIUtil.hide(continueBtn);
    } else if (
      this.gameFrame.round == this.gameFrame.roundsInModule &&
      this.gameFrame.game == this.gameFrame.gamesPerRound
    ) {
      this.gameFrame.disableNextBtn = true;
      this.gameFrame.disableMoreBtn = true;
      UIUtil.show(continueBtn);
      UIUtil.createButton<{
        value?: unknown;
        btnHover: createjs.DisplayObject;
      }>(
        continueBtn,
        this.continueVIF.bind(this),
        0,
        this.onButtonOver.bind(this),
        this.onButtonOut.bind(this),
      );
      UIUtil.hide(continueBtn.btnHover);
    } else {
      this.gameFrame.disableNextBtn = false;
      UIUtil.hide(continueBtn);
    }

    UIUtil.hide(this.gameStage, 500);
    this.resetStartButton();
  }

  /* Sounds */

  protected loadSounds(): void {
    for (const [id, file] of Object.entries(VIF_BOARD_SOUND_FILES)) {
      this.gameFrame.loadSound(file, id);
    }
  }

  /* Interface */

  protected resetStartButton(): void {
    UIUtil.show(this.gameStage.startButton);
    UIUtil.setFrame(this.gameStage.startButton, 1);
    UIUtil.enable(this.gameStage.startButton);
  }

  protected enableStartButton(): void {
    if (this.clockTimer.isRunning()) {
      this.clockTimer.stop();
    }
    const btn = this.gameStage.startButton;
    UIUtil.show(btn);
    UIUtil.setFrame(btn, 1);
    UIUtil.enable(btn);
  }

  protected onTick(): void {
    this.clockSeconds--;
    ValueText.displayText(
      this.gameStage.startButton.clockDisplay,
      this.clockSeconds,
      0,
    );

    if (this.clockSeconds < 1) {
      this.clockTimer.stop();
      this.timeOut();
    }
  }

  protected timeOut(): void {
    this.board.enabled = false;
    this.releasePiece();
    LoadUtil.playSound('timeout');
    UIUtil.useHandCursor(this.board, false);
    this.onTimedOut();
  }

  protected gameOver(): void {
    if (this.clockTimer.isRunning()) {
      this.clockTimer.stop();
    }

    this.board.enabled = false;
    UIUtil.useHandCursor(this.board, false);
    this.gameFrame.score = this.gameFrame.scoreMax;
    this.gameFrame.endGame();
  }

  protected onButtonOver(
    e: TargettedEvent<{ btnHover: createjs.DisplayObject }>,
  ): void {
    if (this.board.enabled) {
      const btn = UIUtil.getEventTarget<{ btnHover: createjs.DisplayObject }>(
        e,
      );
      UIUtil.show(btn.btnHover, 100);
    }
  }

  protected onButtonOut(
    e: TargettedEvent<{ btnHover: createjs.DisplayObject }>,
  ): void {
    if (this.board.enabled) {
      const btn = UIUtil.getEventTarget<{ btnHover: createjs.DisplayObject }>(
        e,
      );
      UIUtil.hide(btn.btnHover, 100);
    }
  }

  /* Piece animation */

  protected unjiggle(e: VifTileHalf): void {
    if (e.jiggleEnabled) {
      UIUtil.delay(this.jiggle.bind(this), this.animMs, [e]);
      UIUtil.tween(
        e,
        'rotation',
        e.newRotation * -1,
        this.animMs / 4,
        'easeOut',
      );
    } else {
      UIUtil.tween(e, 'rotation', 0, this.animMs / 4, 'easeOut');
    }
  }

  protected jiggle(e: VifTileHalf): void {
    if (e.jiggleEnabled) {
      e.newRotation = Chance.randomNumber(
        -this.jiggleRotationRange,
        this.jiggleRotationRange,
      );
      UIUtil.delay(this.unjiggle.bind(this), this.animMs, [e]);
      UIUtil.tween(e, 'rotation', e.newRotation, this.animMs / 4, 'easeIn');
    } else {
      UIUtil.tween(e, 'rotation', 0, this.animMs / 4, 'easeIn');
    }
  }

  /* Board interaction */

  protected onBoardMouseUp(e: createjs.MouseEvent): void {
    if (this.board.enabled) {
      if (this.dragging) {
        const coord = this.getMouseCoordinates(e);
        this.currCol = coord.col;
        this.currRow = coord.row;
      }
      if (this.currCol == this.startCol && this.currRow == this.startRow) {
        this.releasePiece();
      } else {
        let OKtoreleasePiece = true;
        if (!this.dragging || this.draggingPiece == null) {
          OKtoreleasePiece = false;
        }

        if (
          this.dragging &&
          this.currCol != null &&
          this.currRow != null &&
          this.canTradePiece()
        ) {
          if (!this.tileLocked[this.currRow]![this.currCol]) {
            OKtoreleasePiece = false;
            this.tradePiece();
          }
        }
        if (OKtoreleasePiece) {
          this.releasePiece();
        }
      }
    } else {
      this.releasePiece();
    }
  }

  /** Releases the currently dragged piece, if any. Subclass-specific. */
  protected abstract releasePiece(): void;

  /* -------------------------------------------------------------------
   * Block-flavored concrete bodies. These were previously duplicated (in a
   * near-identical but not-quite-shared form) on `BlockBoardEngine`; they
   * now live once, here, as the base's default implementation, driven by
   * `this.geometry` plus the hooks declared above. `RulesBoardEngine`
   * overrides every one of these (`startGame`/`setupGame`/`nextProblem`/
   * `onBoardMouseDown`/`onBoardMouseMove`/`getMouseCoordinates`/
   * `runAnimation`/`resetAnimation`) with its own body, so none of this
   * section is reachable from Rules and its behavior is unaffected.
   * ------------------------------------------------------------------- */

  protected startGame(): void {
    this.loadSounds();
    UIUtil.show(this.gameStage, 500);

    this.onStartGameBegin();

    this.gameFrame.scoreboard.hideMeter();
    this.gameFrame.scoreboard.hideMusicButton();

    this.gameFrame.scoreboard.updateRound(this.gameFrame.round);

    this.blocker = this.gameStage.blocker;
    this.blockerX = this.blocker.x;
    this.blockerY = this.blocker.y;
    this.blockerH = this.blocker.height;
    this.blockerW = this.blocker.width;
    UIUtil.fadeTo(this.blocker, 0, this.animMs);
    this.onBeforeHideMobilePiece();
    UIUtil.hide(this.gameStage.mPiece);

    this.setupGame();
  }

  protected setupGame(): void {
    this.intLayer = this.gameFrame.game;
    this.resetAllTiles(false); // added post port to fix bug; not part of original flow
    this.onSetupGame();
  }

  protected nextProblem(): void {
    if (this.shouldStartClock()) {
      if (this.clockTimer.isRunning()) {
        this.clockTimer.stop();
      }

      this.clockTimer.start();
      this.clockSeconds = this.startSeconds[this.gameFrame.round - 1]!;
      ValueText.whiten(this.gameStage.startButton.clockDisplay);
      ValueText.displayText(
        this.gameStage.startButton.clockDisplay,
        this.clockSeconds,
      );
    } else {
      UIUtil.hide(this.gameStage.startButton);
    }

    this.onNextProblemFirstTime();

    this.populateProblem();

    this.intCorrect = 0;

    this.tileLocked = [];
    for (let r = 0; r < this.gridRows; r++) {
      this.tileLocked.push(new Array<number>(this.gridCols).fill(0));
    }

    this.blocker = this.gameStage.blocker;
    this.blockerX = this.blocker.x;
    this.blockerY = this.blocker.y;
    this.blockerH = this.blocker.height;
    this.blockerW = this.blocker.width;
    UIUtil.fadeTo(this.blocker, 0, this.animMs);

    this.resetAllTilesToFirstFrame();

    const chosenIndices = pickDistinctIndices(
      this.bottomAnswers.length,
      this.gridRows * this.gridCols,
      Math.random,
      this.chosenExclude(),
    );
    this.recordChosen(chosenIndices);
    this.tEqValues = chosenIndices.map((idx) => this.bottomAnswers[idx]!);

    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const settingTile = cellAt(this.board, row, col);
        settingTile.t.jiggleEnabled = true;
        UIUtil.delay(this.jiggle.bind(this), this.animMs, [settingTile.t]);
        this.fillTopHalf(row, col, chosenIndices[row * this.gridCols + col]!);
      }
    }

    const selected = shuffleAvoidingFixedPositions(this.tEqValues, Math.random);

    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        this.fillBottomAnswer(row, col, selected[row * this.gridCols + col]!);
      }
    }

    this.tileOrigAnswers = selected;
    UIUtil.useHandCursor(this.board);
    UIUtil.onMouseMove(
      this.board,
      this.onBoardMouseDown.bind(this),
      this.onBoardMouseMove.bind(this),
      this.onBoardMouseUp.bind(this),
    );

    this.finalizeNextProblem();
  }

  protected onBoardMouseDown(e: createjs.MouseEvent): void {
    if (!this.dragging && this.board.enabled && this.canStartDrag()) {
      const coord = this.getMouseCoordinates(e);
      if (!this.tileLocked[coord.row]![coord.col]) {
        this.draggingPiece = cellAt(this.board, coord.row, coord.col).t;
        this.startX = this.draggingPiece.x;
        this.startY = this.draggingPiece.y;
        this.startRow = coord.row;
        this.startCol = coord.col;
        this.currRow = coord.row;
        this.currCol = coord.col;

        UIUtil.hide(this.draggingPiece);
        const point = UIUtil.getMousePosition(e);
        const xypoint = globalToLocalXY(
          this.mobilePiece.parent,
          point.x,
          point.y,
        );
        this.startmX = xypoint[0]!;
        this.startmY = xypoint[1]!;
        UIUtil.moveTo(this.mobilePiece, xypoint[0] + 30, xypoint[1] + 50);
        UIUtil.show(this.mobilePiece, 100);
        ValueText.displayText(
          this.mobilePiece.t.textb,
          this.tileOrigAnswers[this.gridCols * this.currRow + this.currCol],
        );
        this.dragging = true;

        if (this.draggingPiece) {
          this.onDragStart();
          UIUtil.bringToFront(this.mobilePiece.parent);
          UIUtil.scaleTo(
            this.mobilePiece,
            this.geometry.mobileScale,
            this.animMs,
          );
          UIUtil.fadeTo(this.mobilePiece, 0.8, this.animMs);
        }
      }
    }
  }

  protected onBoardMouseMove(e: createjs.MouseEvent): void {
    if (this.dragging && this.board.enabled) {
      const point = UIUtil.getMousePosition(e);
      const boardPoint = UIUtil.getMousePosition(e, this.board);

      let tRow: number | null = null;
      let tCol: number | null = null;

      if (this.draggingPiece) {
        const { outOfBounds, colBands, rowBands } = this.geometry;
        if (
          boardPoint.x < outOfBounds.minX ||
          boardPoint.x > outOfBounds.maxX ||
          boardPoint.y < outOfBounds.minY ||
          boardPoint.y > outOfBounds.maxY
        ) {
          this.releasePiece();
        } else {
          UIUtil.bringToFront(this.mobilePiece.parent);

          for (let col = 0; col < colBands.length; col++) {
            const band = colBands[col]!;
            if (boardPoint.x >= band.min && boardPoint.x <= band.max) {
              tCol = col;
              break;
            }
          }
          for (let row = 0; row < rowBands.length; row++) {
            const band = rowBands[row]!;
            if (boardPoint.y >= band.min && boardPoint.y <= band.max) {
              tRow = row;
              break;
            }
          }

          const xypoint = globalToLocalXY(
            this.mobilePiece.parent,
            point.x,
            point.y,
          );
          UIUtil.moveTo(this.mobilePiece, xypoint[0] + 30, xypoint[1] + 50);

          let changingTile;
          if (this.currRow != tRow || this.currCol != tCol) {
            this.fadeAllTiles(1, this.animMs / 2);
            this.currCol = tCol;
            this.currRow = tRow;
            if (this.currRow != null && this.currCol != null) {
              if (
                (this.currCol != this.startCol ||
                  this.currRow != this.startRow) &&
                !this.tileLocked[this.currRow]![this.currCol]
              ) {
                changingTile = cellAt(this.board, this.currRow, this.currCol);
                UIUtil.tween(
                  changingTile,
                  'alpha',
                  this.hoverAlpha,
                  this.animMs / 2,
                );
              }
            }
          }
        }
      }
    }
  }

  /**
   * Alpha applied to a tile as the dragged piece hovers over it, used by the
   * base's `onBoardMouseMove`. `BlockBoardEngine` subclasses override this
   * getter directly (AddSub: 0.7, MulDiv: 0.2); `RulesBoardEngine` overrides
   * `onBoardMouseMove` entirely and never reads this.
   */
  protected get hoverAlpha(): number {
    return 1;
  }

  protected getMouseCoordinates(e: createjs.MouseEvent): {
    row: number;
    col: number;
  } {
    const point = UIUtil.getMousePosition(e); //removed draggingPiece (does nothing)
    const { cellWidth, cellHeight, capToGrid } = this.geometry.mouseToCell;
    return mouseToCell(point.x, point.y, {
      cellWidth,
      cellHeight,
      maxRow: capToGrid ? this.gridRows - 1 : undefined,
      maxCol: capToGrid ? this.gridCols - 1 : undefined,
    });
  }

  protected runAnimation(): void {
    UIUtil.fadeTo(this.blocker, 1, 100);

    UIUtil.tween(this.blocker, 'x', 650, this.animMs * 2);
    UIUtil.tween(this.blocker, 'y', 500, this.animMs * 2);
    UIUtil.tween(this.blocker, 'width', 1, this.animMs * 2);
    UIUtil.tween(
      this.blocker,
      'height',
      1,
      this.animMs * 2,
      undefined,
      this.resetAnimation.bind(this),
    );
    this.onRunAnimationMeterUpdate();
    this.intLayer++;
  }

  /** Called inside `runAnimation`, right before `intLayer++`. Block default: no-op. */
  protected onRunAnimationMeterUpdate(): void {}

  protected resetAnimation(): void {
    this.blocker.x = this.blockerX;
    this.blocker.y = this.blockerY;
    this.blocker.alpha = 0;
    this.blocker.width = this.blockerW;
    this.blocker.height = this.blockerH;
    if (this.intLayer <= 4) {
      this.resetAllTiles(true);
    } else {
      this.resetAllTiles(false);
    }
  }

  /* Answer checking */

  protected correctAnswer(): void {
    const checkingPiece = cellAt(this.board, this.currRow!, this.currCol!);
    checkingPiece.t.jiggleEnabled = false;
    UIUtil.setFrame(checkingPiece, 3);
    const topHalf = checkingPiece.text3;
    const bottomHalf = checkingPiece.t;
    UIUtil.setFrame(bottomHalf, this.bottomHalfCorrectFrame());
    const bottomText = bottomHalf.textb;
    ValueText.setColor(topHalf, this.correctColor);
    ValueText.setColor(bottomText, this.correctColor);
    this.intCorrect++;
    this.playCorrectSound();
    this.checkForEnd();
  }

  protected incorrectAnswer(): void {
    const checkingPiece = cellAt(this.board, this.currRow!, this.currCol!);
    UIUtil.setFrame(checkingPiece, 2);
    LoadUtil.playSound('incorrect');
    UIUtil.tween(
      checkingPiece,
      'alpha',
      0,
      800,
      undefined,
      this.resetGame.bind(this),
    );
  }

  protected resetGame(): void {
    this.resetAllTiles(true);
  }

  protected checkForBonus(t: string | number): void {
    if (t == this.tEqValueAt(this.gridCols * this.startRow! + this.startCol!)) {
      this.intCorrect++;
      const checkingPiece = cellAt(this.board, this.startRow!, this.startCol!);
      checkingPiece.t.jiggleEnabled = false;
      this.onBonusSoundQueue();
      UIUtil.setFrame(checkingPiece, 3);
      const topHalf = checkingPiece.text3;
      const bottomHalf = checkingPiece.t;
      UIUtil.setFrame(bottomHalf, this.bottomHalfCorrectFrame());
      const bottomText = bottomHalf.textb;
      ValueText.setColor(topHalf, this.correctColor);
      ValueText.setColor(bottomText, this.correctColor);
      this.tileLocked[this.startRow!]![this.startCol!] = 1;
      this.checkForEnd();
    }
  }

  /* Tile grid resets */

  protected resetAllTilesToFirstFrame(): void {
    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const resettingTile = cellAt(this.board, row, col);
        UIUtil.setFrame(resettingTile, 1);
        UIUtil.fadeTo(resettingTile, 1, 1);
        UIUtil.setFrame(resettingTile.t, this.tileBackFrame());
      }
    }
  }

  /**
   * Fades every tile in the grid's alpha to `alpha` over `ms` ms. Unifies
   * Block's former `unFadeAllTiles()` (always `(1, animMs / 2)` via
   * `UIUtil.tween`) and Rules's former `makeTilesVisible(vis, tm)` (any
   * `vis`/`tm`, via `UIUtil.fadeTo`) into one shared primitive built on
   * `UIUtil.fadeTo`, which is a strict superset of `UIUtil.tween(...,
   * 'alpha', ...)` (it additionally makes a hidden tile visible first).
   * Call sites keep their original alpha/ms values; only the mechanism is
   * merged.
   */
  protected fadeAllTiles(alpha: number, ms: number): void {
    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const tile = cellAt(this.board, row, col);
        UIUtil.fadeTo(tile, alpha, ms);
      }
    }
  }

  protected resetAllTiles(activate: boolean): void {
    this.resetAllTilesToFirstFrame();
    this.fadeAllTiles(1, this.animMs / 2);
    for (let row = 0; row < this.gridRows; row++) {
      for (let col = 0; col < this.gridCols; col++) {
        const resettingTile = cellAt(this.board, row, col);
        this.onResettingTile(resettingTile);
        UIUtil.setFrame(resettingTile.t, 1);
        ValueText.displayText(resettingTile.t.textb, '', 1);
        ValueText.displayText(resettingTile.text3, '', 1);
      }
    }
    if (activate) {
      this.onActivateAfterReset();
    } else {
      if (this.clockTimer.isRunning()) {
        this.clockTimer.stop();
      }
    }
  }
}
